import * as React from 'react';
import Hls, { type ErrorData, type ManifestParsedData, type Events as HlsEvents } from 'hls.js';
import { getCguardAccessToken } from '@cguard/auth';
import { cn } from '@cguard/design-system';
import { useTranslation } from 'react-i18next';
import { VideoStateOverlay } from './video-state-overlay';

/**
 * `<HlsVideoPlayer/>` — the shared LL-HLS live tile.
 *
 * Models the live lifecycle states (loading while the stream token is pending,
 * offline with no stream yet, token fetch failed) and drives MSE playback
 * through hls.js. It renders a small state badge rather than native controls so
 * a 16-tile grid stays calm, and forwards `className` so each tile fits its slot.
 *
 * ADR-0069 codec lock — H.264 only: there is no native Safari `canPlayType`
 * fallback (that would bypass codec inspection; the only place we read
 * `videoCodec` is the hls.js `MANIFEST_PARSED` handler), and on
 * `MANIFEST_PARSED` we filter `data.levels[]` for any non-AVC `videoCodec`.
 * If a non-AVC level appears we surface a `codec_mismatch` overlay instead
 * of letting the player render a black tile. A future surface that needs
 * Safari native HLS must prefetch and parse the master manifest's `CODECS=`
 * attribute against `^avc1\.` before assigning `video.src`.
 */
export interface HlsVideoPlayerProps {
  /** Signed LL-HLS playlist URL. `null` while the stream-token query is pending or absent. */
  streamUrl: string | null;
  /** Used as the `<video>` accessible name + state-overlay label. */
  cameraName: string;
  /** Optional className applied to the outer wrapper (sizing). */
  className?: string;
  /** Browser autoplay policies require muted=true; default `true`. */
  muted?: boolean;
  /** Default `true` so the grid plays as soon as the manifest parses. */
  autoplay?: boolean;
  /**
   * Explicit token-fetch error, surfaced from the parent's
   * `useCameraStreamToken` query so the tile can render a distinct overlay
   * (auth required vs generic stream error vs offline). When set, this takes
   * precedence over `streamUrl` — the player will NOT attempt to play.
   */
  tokenError?: { kind: 'auth' | 'network' | 'other'; message: string } | null;
  /**
   * Optional access to the live <video> element. Consumers use this for
   * read-only frame sampling without reaching through the DOM.
   */
  onVideoElement?: (video: HTMLVideoElement | null) => void;
  /** Reports the rendered player state to larger viewer surfaces. */
  onStateChange?: (state: HlsPlayerState) => void;
}

export type HlsPlayerState = 'offline' | 'loading' | 'playing' | 'error';
type ErrorKind = 'codec_mismatch' | 'token_auth' | 'token_other' | 'stream' | 'unsupported';

export function HlsVideoPlayer({
  streamUrl,
  cameraName,
  className,
  muted = true,
  autoplay = true,
  tokenError = null,
  onVideoElement,
  onStateChange,
}: HlsVideoPlayerProps): React.JSX.Element {
  const { t } = useTranslation('common');
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const [state, setState] = React.useState<HlsPlayerState>(streamUrl ? 'loading' : 'offline');
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [errorKind, setErrorKind] = React.useState<ErrorKind | null>(null);
  // Bumped by Retry: the playback effect depends on it, so its cleanup
  // destroys the current Hls instance and the effect builds a fresh one.
  const [retryToken, setRetryToken] = React.useState(0);
  const handleVideoRef = React.useCallback(
    (video: HTMLVideoElement | null): void => {
      videoRef.current = video;
      onVideoElement?.(video);
    },
    [onVideoElement],
  );

  // Token-fetch error from the parent overrides everything else: never
  // attempt MSE playback if we couldn't even get a signed URL.
  const tokenErrorKind = tokenError?.kind ?? null;
  const tokenErrorMessage = tokenError?.message ?? null;

  React.useEffect(() => {
    onStateChange?.(state);
  }, [onStateChange, state]);

  React.useEffect(() => {
    if (tokenErrorKind) {
      // The parent's raw error text is a diagnostic, not overlay copy.
      if (tokenErrorMessage) console.warn(`[HlsVideoPlayer] ${cameraName}:`, tokenErrorMessage);
      setState('error');
      setErrorKind(tokenErrorKind === 'auth' ? 'token_auth' : 'token_other');
      setErrorMessage(tokenErrorKind === 'network' ? t('video.networkError') : null);
      return;
    }
    if (!streamUrl) {
      setState('offline');
      setErrorKind(null);
      setErrorMessage(null);
      return;
    }
    const video = videoRef.current;
    if (!video) return;

    setState('loading');
    setErrorKind(null);
    setErrorMessage(null);

    let cancelled = false;
    const markPlaying = (): void => {
      if (cancelled) return;
      setState('playing');
    };
    // Raw player and hls.js diagnostics go to the console only; the overlay
    // shows a catalog sentence the operator can act on.
    const markStreamError = (message: string | null, diagnostic: unknown): void => {
      if (cancelled) return;
      console.warn(`[HlsVideoPlayer] ${cameraName}:`, diagnostic);
      setState('error');
      setErrorKind('stream');
      setErrorMessage(message);
    };
    const mediaError = t('video.mediaError');
    const startPlayback = (): void => {
      if (!autoplay) {
        markPlaying();
        return;
      }
      video.muted = muted;
      video.playsInline = true;
      let playPromise: Promise<void> | undefined;
      try {
        playPromise = video.play();
      } catch (err) {
        markStreamError(mediaError, err);
        return;
      }
      if (playPromise === undefined) return;
      void playPromise.then(markPlaying).catch((err: unknown) => {
        markStreamError(mediaError, err);
      });
    };

    if (isProgressiveMp4(streamUrl)) {
      const handleError = (): void => markStreamError(mediaError, video.error);
      video.addEventListener('playing', markPlaying);
      video.addEventListener('error', handleError);
      video.src = streamUrl;
      video.loop = true;
      startPlayback();
      return () => {
        cancelled = true;
        video.removeEventListener('playing', markPlaying);
        video.removeEventListener('error', handleError);
        video.removeAttribute('src');
        video.load();
      };
    }

    // ADR-0069 — MSE-only path. Native Safari HLS would bypass codec
    // inspection (the only place we read `videoCodec` is the hls.js
    // `MANIFEST_PARSED` handler below).
    if (!Hls.isSupported()) {
      setState('error');
      setErrorKind('unsupported');
      setErrorMessage(t('video.unsupported'));
      return;
    }
    // hls.js config tuned for the 4×4 (16-tile) grid worst case.
    // See `apps/operator-console/docs/streaming-budget.md` for the budget
    // model. Each knob is justified individually below; defaults are not
    // safe at this concurrency.
    const hls = new Hls({
      // The signed stream token is bound to the CGuard user that minted it.
      // Send the current bearer only to the same-origin central proxy; direct
      // edge/demo HLS URLs must never receive the user's access token.
      xhrSetup: (xhr, url) => {
        const requestUrl = new URL(url, window.location.href);
        if (
          requestUrl.origin === window.location.origin &&
          requestUrl.pathname.startsWith('/v1/cameras/live/')
        ) {
          const bearer = getCguardAccessToken();
          if (bearer) xhr.setRequestHeader('Authorization', `Bearer ${bearer}`);
        }
      },
      // LL-HLS is the Tier-B contract (see memory
      // `feedback_streaming_architecture` 2026-04-27): MediaMTX central
      // serves CMAF/LL-HLS playlists targeting 2–4 s glass-to-glass.
      lowLatencyMode: true,
      // Single biggest win on multi-tile grids: move demuxing + remuxing
      // into a worker so the main thread stays available for React /
      // raf / input handling. With 16 tiles each running on the main
      // thread, scrolling and layout-flip interactions visibly stutter.
      // Default `true` upstream, but pin it explicitly so a future
      // upgrade can't silently regress us.
      enableWorker: true,
      // Forward buffer: target seconds of media held ahead of the
      // playhead. Default 30 s × 16 tiles = ~480 s of decoded video in
      // flight; on a 1080p main stream that's hundreds of MB. 6 s is
      // enough headroom for LL-HLS jitter (we target ~2 s latency, so
      // 6 s = 3× target — comfortable) while keeping the per-tile
      // memory ceiling tight.
      maxBufferLength: 6,
      // Stretch ceiling under stall recovery: hls.js will grow the
      // forward buffer up to this many seconds if it detects the
      // playhead is starving. 12 s caps the growth at 2× the steady
      // state; default 600 s lets a single misbehaving tile eat the
      // memory budget for the whole grid.
      maxMaxBufferLength: 12,
      // Hard cap in BYTES on the forward buffer per tile. Acts as the
      // belt-and-braces guard when seconds-based caps can't bound a
      // suddenly-high-bitrate variant. 30 MB × 16 tiles ≈ 480 MB
      // forward-buffer ceiling for the grid — fits comfortably in a
      // single Chrome renderer process. Default 60 MB doubles that.
      maxBufferSize: 30 * 1000 * 1000,
      // LL-HLS sync target: how far behind the live edge the player
      // tries to stay. 2 s matches the Tier-B latency target.
      liveSyncDuration: 2,
      // Outer bound before hls.js gives up and jumps to the live edge.
      // 6 s = 3× target, same headroom logic as maxBufferLength.
      liveMaxLatencyDuration: 6,
      // Back-buffer trim. Default 30 s × 16 tiles = ~480 s of decoded
      // past frames held for instant seek. Operators rarely seek
      // backward on the grid (the forensic surface owns that flow,
      // Tier C). 10 s lets a brief scrub-back work without inviting GC
      // pauses that show up as grid-wide frame drops.
      backBufferLength: 10,
    });

    video.addEventListener('playing', markPlaying);
    hls.attachMedia(video);
    hls.loadSource(streamUrl);

    const onManifest = (
      _event: typeof HlsEvents.MANIFEST_PARSED,
      data: ManifestParsedData,
    ): void => {
      // ADR-0069 H.264-only enforcement: every level must advertise an AVC
      // codec string (RFC 6381 strings start with `avc`). An empty
      // `videoCodec` is treated as "unknown but assumed AVC" — the master
      // playlist normally carries `CODECS=` and hls.js parses it; manifests
      // that omit it predate our edge pipeline and we tolerate them rather
      // than block here.
      const levels = data.levels ?? [];
      const nonAvc = levels.filter(
        (l) => l.videoCodec && !l.videoCodec.toLowerCase().startsWith('avc'),
      );
      if (nonAvc.length > 0) {
        setState('error');
        setErrorKind('codec_mismatch');
        setErrorMessage(t('video.codecMismatchDetail'));
        return;
      }
      startPlayback();
    };
    const onFatal = (_evt: unknown, data: ErrorData): void => {
      if (!data.fatal) return;
      markStreamError(
        data.type === Hls.ErrorTypes.NETWORK_ERROR
          ? t('video.networkError')
          : data.type === Hls.ErrorTypes.MEDIA_ERROR
            ? mediaError
            : null,
        data.details,
      );
    };

    hls.on(Hls.Events.MANIFEST_PARSED, onManifest);
    hls.on(Hls.Events.ERROR, onFatal);

    return () => {
      cancelled = true;
      video.removeEventListener('playing', markPlaying);
      hls.off(Hls.Events.MANIFEST_PARSED, onManifest);
      hls.off(Hls.Events.ERROR, onFatal);
      hls.destroy();
    };
  }, [autoplay, cameraName, muted, retryToken, streamUrl, tokenErrorKind, tokenErrorMessage, t]);

  const retry = React.useCallback(() => setRetryToken((n) => n + 1), []);

  return (
    <div
      className={cn(
        'relative isolate overflow-hidden rounded-md border border-[var(--color-hairline)] bg-[var(--color-graphite-950)]',
        className,
      )}
      data-testid="hls-video-player"
      data-state={state}
      data-error-kind={state === 'error' ? errorKind : undefined}
    >
      <video
        ref={handleVideoRef}
        aria-label={cameraName}
        className="block aspect-video h-full w-full object-contain bg-[var(--color-graphite-950)]"
        muted={muted}
        autoPlay={autoplay}
        playsInline
      />
      {state === 'loading' && <VideoStateOverlay tone="connecting" label={t('video.connecting')} />}
      {state === 'offline' && <VideoStateOverlay tone="offline" label={t('video.offline')} />}
      {state === 'error' && (
        <VideoStateOverlay
          tone={errorKind === 'codec_mismatch' ? 'codec_mismatch' : 'error'}
          label={
            errorKind === 'codec_mismatch'
              ? t('video.codecMismatch')
              : errorKind === 'token_auth'
                ? t('video.tokenError.auth')
                : errorKind === 'token_other'
                  ? t('video.tokenError.generic')
                  : t('video.unavailable')
          }
          detail={errorMessage}
          {...(errorKind === 'stream' ? { onRetry: retry, retryLabel: t('video.retry') } : {})}
        />
      )}
    </div>
  );
}

function isProgressiveMp4(streamUrl: string): boolean {
  try {
    return new URL(streamUrl, window.location.href).pathname.endsWith('.mp4');
  } catch {
    return false;
  }
}
